import React, { useState, useEffect } from 'react';
import { EmailMarketingForm } from '../types';
import { Mail, CheckCircle, ArrowRight, Shield, Download, Gift, Star, Zap, Phone, User, Tag, Award } from 'lucide-react';

interface EmailMarketingFormWidgetProps {
  form: EmailMarketingForm;
  apiUrl?: string;
  onSuccess?: (subscriberData: { name: string; email: string; phone?: string }) => void;
  className?: string;
  variant?: 'sidebar' | 'content_top' | 'content_mid' | 'content_bottom' | 'modal' | 'default';
}

export const EmailMarketingFormWidget: React.FC<EmailMarketingFormWidgetProps> = ({
  form,
  apiUrl = '/api',
  onSuccess,
  className = '',
  variant
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const placement = variant || form.placement || 'sidebar';
  const fields = form.fields_config || { askName: true, askPhone: false, askConsent: false };
  const design = form.design_config || { theme: 'modern', primaryColor: '#2563eb', bgColor: '#ffffff', borderRadius: '16px' };
  const primaryColor = design.primaryColor || '#2563eb';

  // Track impression once per session
  useEffect(() => {
    if (!form.id) return;
    const key = `imp_form_${form.id}`;
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      fetch(`${apiUrl}?action=track_form_impression`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: form.id })
      }).catch(() => {});
    }
  }, [form.id, apiUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (!email || !email.includes('@')) {
      setErrorMessage('Por favor, informe um e-mail válido.');
      return;
    }

    if (fields.askConsent && !consent) {
      setErrorMessage('Por favor, aceite os termos para continuar.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}?action=save_marketing_subscriber`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          form_id: form.id,
          tags: form.tags || ''
        })
      });

      const data = await res.json();
      if (res.ok && (data.success || data.id)) {
        setSubmitted(true);
        if (onSuccess) {
          onSuccess({ name, email, phone });
        }
      } else {
        setErrorMessage(data.error || 'Erro ao enviar cadastro. Tente novamente.');
      }
    } catch (err: any) {
      setErrorMessage('Erro de conexão. Verifique sua internet.');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    switch (design.icon) {
      case 'Download': return <Download size={20} />;
      case 'Gift': return <Gift size={20} />;
      case 'Star': return <Star size={20} />;
      case 'Zap': return <Zap size={20} />;
      default: return <Mail size={20} />;
    }
  };

  const getBadgeIcon = (text?: string) => {
    const norm = (text || '').toLowerCase();
    if (norm.includes('bônus') || norm.includes('bonus') || norm.includes('presente') || norm.includes('grátis') || norm.includes('gratis')) {
      return <Gift size={13} className="shrink-0" />;
    }
    if (norm.includes('vip') || norm.includes('exclusiv') || norm.includes('premium')) {
      return <Award size={13} className="shrink-0" />;
    }
    if (norm.includes('desconto') || norm.includes('oferta') || norm.includes('vantag') || norm.includes('%')) {
      return <Tag size={13} className="shrink-0" />;
    }
    return <Gift size={13} className="shrink-0" />;
  };

  // Success state
  if (submitted) {
    return (
      <div 
        id={`email-form-${form.id}-success`}
        className={`p-8 text-center rounded-2xl border transition-all animate-fade-in ${
          design.theme === 'dark' 
            ? 'bg-slate-900 border-slate-800 text-white' 
            : 'bg-emerald-50/90 border-emerald-200 text-emerald-950'
        } ${className}`}
      >
        <div className="w-14 h-14 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30">
          <CheckCircle size={28} />
        </div>
        <h4 className="text-xl font-black mb-2 tracking-tight">
          Inscrição Confirmada! 🎉
        </h4>
        <p className="text-sm opacity-90 leading-relaxed max-w-sm mx-auto">
          Enviamos uma mensagem de confirmação para <strong>{email}</strong>. Fique atento à sua caixa de entrada e spam!
        </p>
      </div>
    );
  }

  // Theme Styling presets
  let containerStyle = 'bg-white border-slate-200/90 text-slate-900 shadow-xl shadow-slate-200/50';
  if (design.theme === 'dark') {
    containerStyle = 'bg-slate-900 border-slate-800 text-white shadow-2xl shadow-black/40';
  } else if (design.theme === 'gradient') {
    containerStyle = 'bg-gradient-to-br from-blue-50 via-indigo-50 to-white border-blue-200 text-slate-900 shadow-xl shadow-blue-500/10';
  } else if (design.theme === 'minimal') {
    containerStyle = 'bg-slate-50/80 border-slate-200 text-slate-900 shadow-sm';
  } else if (design.theme === 'amber') {
    containerStyle = 'bg-amber-50 border-amber-200 text-amber-950 shadow-xl shadow-amber-500/10';
  }

  // Content Top/Mid/Bottom horizontal layouts vs Sidebar vertical
  const isHorizontal = placement === 'content_top' || placement === 'content_mid';

  return (
    <div
      id={`email-form-widget-${form.id}`}
      className={`relative overflow-hidden border p-6 sm:p-7 transition-all rounded-3xl ${containerStyle} ${className}`}
      style={{
        borderRadius: design.borderRadius || '24px'
      }}
    >
      {/* Decorative ambient glow */}
      <div 
        className="absolute -top-12 -right-12 w-36 h-36 rounded-full blur-2xl opacity-15 pointer-events-none"
        style={{ backgroundColor: primaryColor }}
      />

      {/* Header section */}
      <div className="relative z-10 mb-5">
        {design.badgeText && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black tracking-wide uppercase mb-3 bg-primary/10 text-primary border border-primary/20">
            {getBadgeIcon(design.badgeText)}
            <span>{design.badgeText}</span>
          </div>
        )}

        <div className="flex items-start gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-md text-white transition-transform hover:scale-105"
            style={{ backgroundColor: primaryColor }}
          >
            {getIcon()}
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-black tracking-tight leading-tight">
              {form.title || 'Receba Nossos Melhores Conteúdos'}
            </h3>
            {form.subtitle && (
              <p className={`mt-1.5 text-xs sm:text-sm leading-relaxed ${design.theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
                {form.subtitle}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Form section */}
      <form onSubmit={handleSubmit} className="relative z-10 space-y-3.5">
        {fields.askName && (
          <div className="relative">
            <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              id={`email-form-${form.id}-input-name`}
              type="text"
              placeholder="Seu primeiro nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 text-sm rounded-xl border transition-all outline-none focus:ring-2 ${
                design.theme === 'dark'
                  ? 'bg-slate-800/80 border-slate-700 text-white placeholder-slate-400 focus:ring-blue-500 focus:border-blue-500'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-primary focus:border-primary'
              }`}
            />
          </div>
        )}

        <div className="relative">
          <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            id={`email-form-${form.id}-input-email`}
            type="email"
            required
            placeholder="Seu melhor e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full pl-10 pr-4 py-3 text-sm rounded-xl border transition-all outline-none focus:ring-2 ${
              design.theme === 'dark'
                ? 'bg-slate-800/80 border-slate-700 text-white placeholder-slate-400 focus:ring-blue-500 focus:border-blue-500'
                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-primary focus:border-primary'
            }`}
          />
        </div>

        {fields.askPhone && (
          <div className="relative">
            <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              id={`email-form-${form.id}-input-phone`}
              type="tel"
              placeholder="WhatsApp com DDD (opcional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 text-sm rounded-xl border transition-all outline-none focus:ring-2 ${
                design.theme === 'dark'
                  ? 'bg-slate-800/80 border-slate-700 text-white placeholder-slate-400 focus:ring-blue-500 focus:border-blue-500'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-primary focus:border-primary'
              }`}
            />
          </div>
        )}

        {fields.askConsent && (
          <label className="flex items-start gap-2.5 text-xs text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-primary focus:ring-primary"
            />
            <span>Concordo em receber comunicações e novidades exclusivas. Cancele a qualquer momento.</span>
          </label>
        )}

        {errorMessage && (
          <p className="text-xs font-bold text-red-500 bg-red-50 dark:bg-red-950/40 p-2.5 rounded-lg border border-red-200 dark:border-red-900 animate-shake">
            {errorMessage}
          </p>
        )}

        <button
          id={`email-form-${form.id}-submit-btn`}
          type="submit"
          disabled={loading}
          className="w-full py-3.5 px-6 rounded-xl font-bold text-sm text-white shadow-lg flex items-center justify-center gap-2 transition-all hover:brightness-110 active:scale-[0.99] disabled:opacity-50 cursor-pointer"
          style={{ backgroundColor: primaryColor }}
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <span>{form.button_text || 'Quero Me Inscrever'}</span>
              <ArrowRight size={16} />
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 pt-1">
          <Shield size={12} className="text-emerald-500" />
          <span>Livre de spam. Seus dados estão 100% seguros.</span>
        </div>
      </form>
    </div>
  );
};

export default EmailMarketingFormWidget;
